import { useCallback, memo, useEffect, useMemo, useRef } from 'react'
import { useParams } from 'next/navigation'
import styled from 'styled-components'
import { AnimatePresence, motion, useAnimation, useInView, type Variants } from 'motion/react'
import { useSlider } from '@/hooks'
import type { TypeCmsProjectInProduct, TypeLang } from '@/schema'
import { mq, vw, getTransition, getP18, imgVariants } from '@/styles'
import { Img, Link } from './commons'

interface ITransitionSlider {
  data: TypeCmsProjectInProduct[]
  imgSize?: 'full' | 'half'
  parentState?: 'small' | 'full'
  parentStateSetter?: (state: 'small' | 'full') => void
  zoom?: boolean,
}

const changeColorVariants = {
  animate: imgSize => ({
    stroke: imgSize === 'full' ? '#FFF' : '#4D4D4D',
    transition: {
      ...getTransition(),
      duration: 1
    }
  })
}

const getSlide = (data, idx, lang, zoom) => {
  if (!data) return {}
  if (zoom) {
    return { url: '#', image: data?.[idx]?.zoom, name: '', display: 'half' }
  }

  const { name, slug, display, header } = data[idx]

  return { url: `/project/${slug[lang]}`, medium: header?.medium, name, display }
}

export const TransitionSlider = memo<ITransitionSlider>(({ data, parentStateSetter, parentState = 'small', zoom = false }) => {
  const { lang }: { lang: TypeLang } = useParams()
  const controls = useAnimation()
  const { currentSlide, triggerRef, nextSlide, prevSlide, setSlide } = useSlider(data.length, zoom)
  const Slide = useMemo(() => getSlide(data, currentSlide, lang, zoom), [data, currentSlide, lang, zoom])
  const ref = useRef(null)
  const textRef = useRef(null)
  const isInView = useInView(ref, { margin: '0px 0px -100px 0px', once: false })
  const textInView = useInView(textRef, { margin: '0px 0px -100px 0px', once: false })

  useEffect(() => {
    if (isInView) {
      controls.start('animate')
    }
  }, [controls, isInView])

  useEffect(() => {
    if (textInView) {
      controls.start('animateText')
    }
  }, [controls, textInView])

  useEffect(() => {
    parentStateSetter(Slide?.display)
    setSlide(0)
  }, [data, Slide?.display, parentStateSetter, setSlide])

  const handleComplete = useCallback(() => {
    parentStateSetter(Slide?.display)
  }, [Slide?.display, parentStateSetter])

  if (!data) return <SliderStyled></SliderStyled>

  return (
    <SliderStyled className='transition-slider' ref={triggerRef}>
      <div className='wrapper'>
        <Link href={Slide?.url}>
          <div onClick={Slide?.url === '#' ? (e) => e.preventDefault() : null}>
            <MediaWrapper ref={ref}>
              <motion.div variants={imgVariants} animate={controls} initial='initial'>
                <AnimatePresence onExitComplete={handleComplete}>
                  <motion.div className={`${Slide?.display} ${currentSlide}`} key={currentSlide} variants={slideVariants} initial='initial' exit='exit' animate='animate'>
                    {Slide?.medium?.image && <Img lazy={false} media={Slide?.medium?.image} />}
                  </motion.div>
                </AnimatePresence>
              </motion.div>
            </MediaWrapper>
            <div className='title-wrapper' ref={textRef}>
              <motion.div variants={textVariants} animate={controls} initial='initial'>
                <motion.p key={currentSlide} variants={titleVariants} initial='initial' exit='exit' animate='animate'>{Slide?.name}</motion.p>
              </motion.div>
            </div>
          </div>
        </Link>
      </div>
      {data.length > 1 && (
        <>
          <button onClick={() => prevSlide()} className='control-btn prev' key={`btn-back${parentState}`}>{Svgs.back(parentState)}</button>
          <button onClick={() => nextSlide()} className='control-btn next' key={`btn-next${parentState}`}>{Svgs.next(parentState)}</button>
        </>
      )}
    </SliderStyled>
  )
})

const SliderStyled = styled.div`
  grid-area: auto / 1 / span 1 / span 6;
  margin: 0 -${vw(23, 'mobile')};
  overflow: hidden;
  position: relative;

  ${mq.greaterThan('tablet')}{
    grid-area: auto / 1 / span 1 / span 12;
    margin: 0;
  }

  img {
    height: ${vw(486, 'mobile')};
    max-width: 100%;
    position: relative;
    width: 100%;

    ${mq.greaterThan('tablet')}{
      height: ${vw(804, 'desktop')};
    }

  }

  .title-wrapper {
    bottom: ${vw(5, 'mobile')};
    left: 50%;
    overflow: hidden;
    position: absolute;
    transform: translateX(-50%);

    ${mq.greaterThan('tablet')}{
      bottom: ${vw(2, 'desktop')};
    }
  }

  p {
    ${getP18()}
    color: var(--color-white);
    opacity: 0;

    ${mq.greaterThan('tablet')}{
      letter-spacing: -0.29px;
    }
  }

  .control-btn {
    left: 0;
    padding: ${vw(24, 'mobile')};
    position: absolute;
    top: 45%;

    &.next {
      right: 0;
      left: auto;
    }

    ${mq.greaterThan('tablet')}{
      padding: ${vw(40, 'desktop')};
      top: calc(50% - ${vw(20, 'desktop')});
    }
  }

  .half {
    img {
      width: ${vw(326, 'mobile')};
      height: ${vw(250, 'mobile')};
      object-fit: contain;
      margin: auto;
      ${mq.greaterThan('tablet')}{
        width: ${vw(667, 'desktop')};
        height: ${vw(510, 'desktop')};

      }
    }


  }
`

const MediaWrapper = styled.div`
  position: relative;
  height: ${vw(250, 'mobile')};
  overflow: hidden;
  /* display: flex;
  align-items: center;
  justify-content: center; */

  ${mq.greaterThan('tablet')} {
    height: ${vw(510, 'desktop')};
  }


`

const textVariants:Variants = {
  initial: {
    y: '20px',
    opacity: 0,
    transition: {
      ...getTransition()
    }
  },
  animateText: {
    y: '0px',
    opacity: 1,
    transition: {
      ...getTransition(),
      ease: [.32,0,.29,.99],
      duration: 1
    }
  },
  exit: {
    y: '20px',
    opacity: 0,
    transition: {
      ...getTransition(),
      duration: .5
    }
  },
}

const slideVariants:Variants = {
  initial: {
    opacity: 0,
    scale: 1.02,
    transition: {
      ...getTransition(),
      delay: 0,
    }
  },
  animate: {
    opacity: 1,
    scale: 1,
    transition: {
      ...getTransition(),
      duration: 1,
      delay: .3,
    }
  },
  exit: {
    opacity: 0,
    transition: {
      ...getTransition(),
      duration: .2,
      delay: 0,
    }
  },
}

const titleVariants:Variants = {
  initial: {
    opacity: 0,
    transition: {
      ...getTransition(),
      delay: 0,
    }
  },
  animate: {
    opacity: 1,
    transition: {
      ...getTransition(),
      duration: 1,
      delay: .6,
    }
  },
  exit: {
    opacity: 0,
    transition: {
      ...getTransition(),
      duration: .2,
      delay: 0,
    }
  },
}



const Svgs = {
  'back': imgSize => (
    <svg width="38px" height="34px" viewBox="0 0 38 34" version="1.1" xmlns="http://www.w3.org/2000/svg">
      <title>i--arrow back</title>
      <g id="Page-1" stroke="none" strokeWidth="1" fill="none" fillRule="evenodd">
        <motion.g id="Home" transform="translate(-39.000000, -2076.000000)" stroke={imgSize === 'full' ? '#4D4D4D' : '#FFFFFF'} strokeWidth="0.5" custom={imgSize} variants={changeColorVariants} animate='animate'>
          <g id="Projects" transform="translate(0.000000, 1689.000000)">
            <g id="i--arrow-back" transform="translate(40.000000, 387.972100)">
              <line x1="0" y1="16.8033" x2="38" y2="16.8033" id="Stroke-8-Copy"></line>
              <polyline id="Stroke-9-Copy" points="16.8035 33.607 0.0005 16.803 16.8035 0"></polyline>
            </g>
          </g>
        </motion.g>
      </g>
    </svg>
  ),
  'next': imgSize => (
    <svg width="38px" height="34px" viewBox="0 0 38 34" version="1.1" xmlns="http://www.w3.org/2000/svg">
      <title>i--arrow next</title>
      <g id="Page-1" stroke="none" strokeWidth="1" fill="none" fillRule="evenodd">
        <motion.g id="Home" transform="translate(-1362.000000, -2077.000000)" stroke={imgSize === 'full' ? '#4D4D4D' : '#FFFFFF'} strokeWidth="0.5" custom={imgSize} variants={changeColorVariants} animate='animate'>
          <g id="Projects" transform="translate(0.000000, 1689.000000)">
            <g id="i--arrow-next" transform="translate(1362.000000, 388.420900)">
              <line x1="38" y1="16.8037" x2="0" y2="16.8037" id="Stroke-6-Copy"></line>
              <polyline id="Stroke-7-Copy" points="21.1968 0 37.9998 16.804 21.1968 33.607"></polyline>
            </g>
          </g>
        </motion.g>
      </g>
    </svg>
  )
}
